/**
 * Rounded bill-number button — the clickable affordance for an affair /
 * dispatch reference in a chat answer (e.g. a dossier's central bill).
 *
 * Replaces the old underlined hyperlink: the bill NUMBER itself sits in a
 * rounded chip that opens the official parlament.ch page in a new tab. Used by
 * chatMarkdown for both affair-type citations and (defensively) any inline
 * bill-number link the model emits.
 *
 * On hover it shows a themed floating tooltip (the same treatment as
 * CitationPill) — the full affair label + source host — instead of the
 * unstyled native browser `title` bubble.
 */
import { useState } from 'react';
import { FileText } from 'lucide-react';
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';
import { safeNavUrl } from '../lib/safeUrl';
import { useT } from '../i18n';

interface BillButtonProps {
  /** Visible label — the bill number, e.g. "25.026". */
  number: string;
  /** Official parlament.ch URL (validated before use). */
  url: string;
  /** Full citation label shown in the tooltip (e.g. "25.026 — Keine 10-Millionen-Schweiz!"). */
  title?: string;
  /** Which citable kind this is — drives the tooltip's type tag. */
  kind?: 'affair' | 'dispatch';
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function BillButton({ number, url, title, kind = 'affair' }: BillButtonProps) {
  const [open, setOpen] = useState(false);
  const t = useT();

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover   = useHover(context, { delay: { open: 0, close: 100 } });
  const focus   = useFocus(context);
  const dismiss = useDismiss(context);
  const role    = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  // Protocol-check the model-supplied URL (Audit H2) — render plain text rather
  // than a broken/unsafe link if it doesn't pass.
  const safe = safeNavUrl(url);
  if (!safe) return <>{number}</>;

  const typeLabel = t(kind === 'dispatch' ? 'citation.type.dispatch' : 'citation.type.affair');
  const host      = hostnameOf(safe);
  // Only show the label line when it adds something beyond the number itself.
  const labelLine = title && title.trim() !== number.trim() ? title : null;

  return (
    <>
      <a
        ref={refs.setReference}
        {...getReferenceProps()}
        href={safe}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="
          inline-flex items-center gap-[3px] align-middle mx-[1px]
          px-[7px] py-[1px] rounded-full no-underline
          text-[11px] font-[family-name:var(--font-mono)] font-semibold leading-[1.5]
          bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/25
          hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]
          transition-colors
        "
        style={{ transform: 'translateY(-0.5px)' }}
      >
        <FileText size={10} className="shrink-0" />
        {number}
      </a>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={{
              ...floatingStyles,
              width: 240,
              maxWidth: 'calc(100vw - 16px)',
              zIndex: 10000,
            }}
            className="
              pointer-events-none
              px-2.5 py-1.5 rounded-[var(--r2)]
              bg-[var(--text-1)] text-white
              shadow-lg
              text-[11px] font-sans leading-snug
              break-words
            "
          >
            <span className="block text-[11px] font-mono uppercase tracking-wider opacity-70 mb-0.5">
              {typeLabel}
            </span>
            <span className="block font-medium">{labelLine ?? number}</span>
            {host && (
              <span className="block text-[9.5px] font-mono opacity-60 mt-0.5 truncate">
                {host}
              </span>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
